import { Table } from 'antd';
import Button from '../../../components/Button';
import React, { useEffect, useState } from 'react';
import EditableRow from '../../../components/book/editable/EditableRow';
import EditableCell from '../../../components/book/editable/EditableCell';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import {
  useDeleteBookMutation,
  useGetBookInformationByBookNoQuery,
} from '../../../api/books/bookManagement.api';
import { successVariant } from '../../../lib/barVariant';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';

type EditableTableProps = Parameters<typeof Table>[0];
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>;
const BookCopyInformation = ({
  selectedRow,
  setSelectedRow,
  campusBookCopyInformation,
  setCampusBookCopyInformation,
  onHandleModifyButton,
  isModify,
}: any) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentCampus: campusCode } = useSelector(
    (state: RootState) => state.auth,
  );
  const { data } = useGetBookInformationByBookNoQuery({
    bookNo: selectedRow?.bookNo ?? 0,
    campusCode,
  });
  const [deleteBook, { isSuccess: isDeleteSuccess }] = useDeleteBookMutation();
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isDeleteValid, setIsDeleteValid] = useState<boolean>(false);
  const copyColumn: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: t('booksManagement.books.column1'),
      dataIndex: 'no',
      align: 'center',
    },
    {
      title: t('booksManagement.books.column48'),
      dataIndex: 'campusName',
      align: 'center',
    },
    {
      title: t('booksManagement.books.column49'),
      dataIndex: 'bookCount',
      align: 'center',
      editable: true,
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const accumulate = (acc: number, current: any) =>
    acc + Number(current.bookCount === '-' ? 0 : current.bookCount);

  const handleSave = (record: any) => {
    setCampusBookCopyInformation(
      campusBookCopyInformation.map((item: any) => {
        if (item.clientCode !== record.clientCode) {
          return item;
        }

        return record;
      }),
    );
  };

  const editColumns = copyColumn.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        bookInfo: data?.data?.bookDto,
        handleSave,
      }),
    };
  });

  const handleBookDeleteButton = () => {
    let sumCount = data.data.bookDuplicateCampusDtos.reduce(accumulate, 0);
    if (sumCount > 0) {
      setIsDeleteValid(true);
      return;
    }

    setIsDelete(true);
  };

  const handleDeleteBook = () => {
    const bookNo = selectedRow?.bookNo;
    deleteBook({ bookNo });
  };

  useEffect(() => {
    if (data?.data) {
      if (
        data?.data?.bookDto?.bookNo &&
        data?.data?.bookDto?.registDiv === 'MU'
      ) {
        setCampusBookCopyInformation(
          data.data.bookDuplicateCampusDtos.map((campus: any) => {
            if (!campus.bookNo) {
              return {
                ...campus,
                bookCount: '-',
              };
            }

            return campus;
          }),
        );
        return;
      }

      setCampusBookCopyInformation(data.data.bookDuplicateCampusDtos);
    }
  }, [data, selectedRow]);

  useEffect(() => {
    if (!isDeleteSuccess) return;

    enqueueSnackbar(t('booksManagement.books.message22'), successVariant);
    setSelectedRow(null);
    setIsDelete(false);
  }, [isDeleteSuccess]);

  return (
    <>
      <div className='comm-table__area'>
        <Table
          columns={editColumns as ColumnTypes}
          size={'small'}
          components={components}
          pagination={false}
          dataSource={campusBookCopyInformation}
          rowKey={(record) => {
            //@ts-ignore
            return record?.clientCode;
          }}
          summary={(pageData: any) => {
            const sumCount = pageData.reduce(accumulate, 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2} align={'center'}>
                    {t('books.text.22')}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align={'center'}>
                    {isNaN(sumCount) ? 0 : sumCount}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          {selectedRow?.bookNo && (
            <Button
              className="btn-basic gray"
              onClick={handleBookDeleteButton}
              text={t('common.button.delete')}
            />
          )}
          <Button
            className={`btn-basic ${isModify ? 'pink' : 'pink-line'}`}
            onClick={onHandleModifyButton}
            text={t(
              `${isModify ? 'common.button.save' : 'common.button.edit'}`,
            )}
          />
        </div>
      </div>

      <ConfirmModal
        visible={isDelete}
        setVisible={setIsDelete}
        onConfirm={handleDeleteBook}
        titleText={t('booksManagement.books.message24')}
        messageText={t('booksManagement.books.message25')}
        cancelText={t('common.button.cancel')}
        confirmText={t('common.button.confirm')}
      />

      <ConfirmModal
        visible={isDeleteValid}
        setVisible={setIsDeleteValid}
        onConfirm={() => setIsDeleteValid(false)}
        titleText={t('booksManagement.books.message24')}
        messageText={t('booksManagement.books.message26')}
        confirmText={t('common.button.confirm')}
      />
    </>
  );
};

export default BookCopyInformation;
